import styled from 'styled-components';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Checkbox, Button } from 'semantic-ui-react';

const CompareAnchorsModal = ({
  open,
  onClose,
  onApply,
  onClear,
  options, // array of string names
  maxSelectable = 5,
  initialSelected = []
}) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set(initialSelected));

  useEffect(() => {
    setSelected(new Set(initialSelected));
  }, [initialSelected, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) {
      next.delete(name);
    } else {
      if (next.size >= maxSelectable) return; // enforce cap
      next.add(name);
    }
    setSelected(next);
  };

  const apply = () => {
    onApply(Array.from(selected));
    onClose();
  };

  const clearAll = () => {
    setSelected(new Set());
    onClear && onClear();
  };

  const isDisabled = (name) => !selected.has(name) && selected.size >= maxSelectable;

  return (
    <Modal onClose={onClose} open={open} size={"small"} closeIcon>
      <Modal.Header style={{ padding: 24 }}>
        <HeaderRow>
          <div>
            <Title>Select up to {maxSelectable} tags to compare</Title>
            <Subtle>Search and choose the specific tag options to include.</Subtle>
          </div>
          <div>
            <SmallButton basic onClick={clearAll}>Clear</SmallButton>
          </div>
        </HeaderRow>
      </Modal.Header>
      <Modal.Content style={{ padding: 24 }}>
        <Input
          value={query}
          onChange={(e, { value }) => setQuery(value)}
          placeholder="Search tags"
          style={{ width: '100%', marginBottom: 16, fontFamily: 'Raleway, sans-serif' }}
        />
        <List>
          {(filtered || []).map((name) => (
            <ListItem key={name} onClick={() => toggle(name)}>
              <Checkbox
                checked={selected.has(name)}
                onChange={() => toggle(name)}
                disabled={isDisabled(name)}
                label={name}
              />
            </ListItem>
          ))}
          {filtered && filtered.length === 0 && (
            <Empty>No matches</Empty>
          )}
        </List>
      </Modal.Content>
      <Modal.Actions style={{ padding: 16 }}>
        <Button onClick={onClose} basic>
          Cancel
        </Button>
        <Button onClick={apply} primary disabled={selected.size === 0}>
          Apply
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CompareAnchorsModal;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  color:#2A3039;
  font-weight:600;
  font-size:16px;
  margin-bottom:4px;
`;

const Subtle = styled.div`
  color:#666D79;
  font-size:12px;
`;

const List = styled.div`
  max-height: 360px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
`;

const Empty = styled.div`
  color:#7E7E7E;
  font-size:12px;
`;

const SmallButton = styled(Button)`
  font-family: 'Raleway', sans-serif !important;
  font-size: 12px !important;
`;

